import { useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, X, Upload, GripVertical } from "lucide-react";
import { ICON_MAP, ICON_OPTIONS, COTIZAR_TARGET_OPTIONS } from "./utils";

const EditableList = ({ items, onChange, placeholder, testidPrefix }) => {
  const update = (i, value) => onChange(items.map((it, idx) => (idx === i ? value : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  const move = (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={item} onChange={(e) => update(i, e.target.value)} className="input-premium" data-testid={`${testidPrefix}-input-${i}`} />
          <Button size="sm" variant="ghost" className="h-8 px-1 text-pearl-muted" onClick={() => move(i, -1)} disabled={i === 0}>↑</Button>
          <Button size="sm" variant="ghost" className="h-8 px-1 text-pearl-muted" onClick={() => move(i, 1)} disabled={i === items.length - 1}>↓</Button>
          <Button size="sm" variant="ghost" className="h-8 px-1 text-red-400" onClick={() => remove(i)} data-testid={`${testidPrefix}-remove-${i}`}><X size={14} /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" className="btn-gold-outline" onClick={add} data-testid={`${testidPrefix}-add-btn`}>
        <Plus size={14} className="mr-1" /> {placeholder}
      </Button>
    </div>
  );
};

const MediaManager = ({ block, uploading, onUpload, onRemove, onReorder, onCaptionChange }) => {
  const fileInputRef = useRef(null);
  const dragIndex = useRef(null);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          className="hidden"
          data-testid={`media-file-input-${block.id}`}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}
        />
        <Button size="sm" className="btn-gold-outline" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid={`media-upload-btn-${block.id}`}>
          <Upload size={14} className="mr-1" /> {uploading ? "Subiendo..." : "Subir foto o video"}
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {(block.media || []).map((m, i) => (
          <div
            key={m.id}
            className="relative border border-gold/20 rounded-lg p-2 bg-night/40 cursor-move"
            draggable
            data-testid={`media-item-${m.id}`}
            onDragStart={() => { dragIndex.current = i; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (dragIndex.current !== null && dragIndex.current !== i) onReorder(dragIndex.current, i); dragIndex.current = null; }}
          >
            <div className="flex items-center justify-between mb-1">
              <GripVertical size={14} className="text-pearl-muted" />
              <Button size="sm" variant="ghost" className="h-6 px-1 text-red-400" onClick={() => onRemove(m)} data-testid={`media-delete-btn-${m.id}`}><Trash2 size={14} /></Button>
            </div>
            {m.tipo === "video" ? (
              <video src={m.url} muted className="w-full h-24 object-cover rounded" />
            ) : (
              <img src={m.url} alt="" className="w-full h-24 object-cover rounded" />
            )}
            <Input
              placeholder="Pie de foto (opcional)"
              value={m.caption || ""}
              className="input-premium mt-2 text-xs h-7"
              data-testid={`media-caption-input-${m.id}`}
              onChange={(e) => onCaptionChange(m.id, e.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const BlockEditor = ({ block, hook }) => {
  const Icon = ICON_MAP[block.icono] || ICON_MAP.camera;
  return (
    <Card className="card-premium border-gold/30" data-testid={`service-block-editor-${block.id}`}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="text-gold flex items-center gap-2"><Icon size={18} /> {block.titulo || "Nuevo bloque"}</CardTitle>
        <div className="flex items-center gap-3">
          <Label className="flex items-center gap-2 text-pearl-muted text-sm">
            <Switch checked={block.visible} onCheckedChange={(v) => hook.updateBlock(block.id, "visible", v)} data-testid={`service-block-visible-switch-${block.id}`} />
            {block.visible ? "Visible" : "Oculto"}
          </Label>
          <Button size="sm" variant="destructive" onClick={() => hook.removeBlock(block.id)} data-testid={`service-block-delete-btn-${block.id}`}><Trash2 size={14} /></Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-pearl-muted text-xs">Título</Label>
            <Input value={block.titulo} className="input-premium" data-testid={`service-block-titulo-${block.id}`}
              onChange={(e) => hook.updateBlock(block.id, "titulo", e.target.value)} />
          </div>
          <div>
            <Label className="text-pearl-muted text-xs">Subtítulo (opcional)</Label>
            <Input value={block.subtitulo || ""} className="input-premium" data-testid={`service-block-subtitulo-${block.id}`}
              onChange={(e) => hook.updateBlock(block.id, "subtitulo", e.target.value)} />
          </div>
          <div>
            <Label className="text-pearl-muted text-xs">Ícono</Label>
            <Select value={block.icono} onValueChange={(v) => hook.updateBlock(block.id, "icono", v)}>
              <SelectTrigger className="input-premium" data-testid={`service-block-icono-select-${block.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ICON_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-pearl-muted text-xs">Qué abre el botón "Cotizar"</Label>
            <Select value={block.cotizar_target || "none"} onValueChange={(v) => hook.updateBlock(block.id, "cotizar_target", v === "none" ? "" : v)}>
              <SelectTrigger className="input-premium" data-testid={`service-block-cotizar-select-${block.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {COTIZAR_TARGET_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-pearl-muted text-xs">Texto corto</Label>
          <Textarea value={block.texto} className="input-premium" rows={2} data-testid={`service-block-texto-${block.id}`}
            onChange={(e) => hook.updateBlock(block.id, "texto", e.target.value)} />
        </div>

        <div>
          <Label className="text-pearl-muted text-xs">Espacio requerido</Label>
          <Input value={block.espacio_requerido} className="input-premium" data-testid={`service-block-espacio-${block.id}`}
            onChange={(e) => hook.updateBlock(block.id, "espacio_requerido", e.target.value)} />
        </div>

        <div>
          <Label className="text-pearl-muted text-xs mb-1 block">Puntos (lista de la tarjeta)</Label>
          <EditableList items={block.puntos} onChange={(v) => hook.updateBlock(block.id, "puntos", v)} placeholder="Agregar punto" testidPrefix={`service-block-${block.id}-puntos`} />
        </div>

        <div>
          <Label className="text-pearl-muted text-xs mb-1 block">Requisitos para el montaje</Label>
          <EditableList items={block.requisitos} onChange={(v) => hook.updateBlock(block.id, "requisitos", v)} placeholder="Agregar requisito" testidPrefix={`service-block-${block.id}-requisitos`} />
        </div>

        <div>
          <Label className="text-pearl-muted text-xs mb-1 block">Fotos y videos</Label>
          <MediaManager
            block={block}
            uploading={hook.uploadingBlockId === block.id}
            onUpload={(file) => hook.handleUpload(block.id, file)}
            onRemove={(media) => hook.removeMedia(block.id, media)}
            onReorder={(from, to) => hook.reorderMedia(block.id, from, to)}
            onCaptionChange={(mediaId, caption) => hook.updateMediaCaption(block.id, mediaId, caption)}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export function ServicesSectionTab({ servicesSectionHook }) {
  const hook = servicesSectionHook;

  if (hook.loading || !hook.section) {
    return <p className="text-pearl-muted" data-testid="services-section-loading">Cargando sección de Servicios...</p>;
  }

  const section = hook.section;

  return (
    <div className="space-y-4" data-testid="services-section-tab">
      <Card className="card-premium border-gold/30">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="flex-1">
            <Label className="text-pearl-muted text-xs">Título de la sección (portada)</Label>
            <Input
              value={section.titulo}
              className="input-premium"
              data-testid="services-section-title-input"
              onChange={(e) => hook.updateSectionField("titulo", e.target.value)}
            />
          </div>
          <Label className="flex items-center gap-2 text-pearl-muted text-sm">
            <Switch checked={section.visible} onCheckedChange={(v) => hook.updateSectionField("visible", v)} data-testid="services-section-visible-switch" />
            {section.visible ? "Sección visible" : "Sección oculta"}
          </Label>
          <Button className="btn-gold" disabled={hook.saving} onClick={hook.save} data-testid="services-section-save-btn">
            {hook.saving ? "Guardando..." : "Guardar"}
          </Button>
        </CardContent>
      </Card>

      {section.bloques.map((block) => <BlockEditor key={block.id} block={block} hook={hook} />)}

      <Button variant="outline" className="btn-gold-outline" onClick={hook.addBlock} data-testid="services-section-add-block-btn">
        <Plus size={14} className="mr-1" /> Agregar bloque de servicio
      </Button>
    </div>
  );
}
